//! Dropdown buttons on a worksheet AutoFilter header row.
//!
//! Excel draws a 13×13 button at the right edge of each header cell with no
//! table chrome (no name pill, header fill, or outline). A funnel replaces
//! the caret when that column's filter is active.

import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import type { CellsSheet } from '@/app/gridGL/cells/CellsSheet';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import type { JsSheetAutoFilter, SheetInfo } from '@/app/quadratic-core-types';
import type { Point } from 'pixi.js';
import { Container, Graphics, Rectangle } from 'pixi.js';

const BUTTON_SIZE = 13;
const BUTTON_MARGIN = 2;
const CARET_SIZE = 3;

interface Control {
  columnIndex: number;
  bounds: Rectangle;
}

const parseAutoFilter = (json: string | null | undefined): JsSheetAutoFilter | undefined => {
  if (!json) return undefined;
  try {
    return JSON.parse(json) as JsSheetAutoFilter;
  } catch (error) {
    console.error('Failed to parse sheet AutoFilter:', error);
    return undefined;
  }
};

export class SheetAutoFilterButtons extends Container {
  private cellsSheet: CellsSheet;
  private graphics: Graphics;
  private controls: Control[] = [];
  private autoFilter: JsSheetAutoFilter | undefined;
  tableCursor: string | undefined;

  constructor(cellsSheet: CellsSheet) {
    super();
    this.cellsSheet = cellsSheet;
    this.graphics = this.addChild(new Graphics());
    this.autoFilter = sheets.getById(cellsSheet.sheetId)?.autoFilter;
    this.redraw();

    events.on('sheetInfoUpdate', this.handleSheetInfoUpdate);
    events.on('sheetOffsetsUpdated', this.handleOffsets);
  }

  destroy() {
    events.off('sheetInfoUpdate', this.handleSheetInfoUpdate);
    events.off('sheetOffsetsUpdated', this.handleOffsets);
    super.destroy({ children: true });
  }

  private handleSheetInfoUpdate = (sheetInfo: SheetInfo) => {
    if (sheetInfo.sheet_id !== this.cellsSheet.sheetId) return;
    this.autoFilter = parseAutoFilter(sheetInfo.auto_filter);
    this.redraw();
  };

  private handleOffsets = (sheetId: string) => {
    if (sheetId !== this.cellsSheet.sheetId) return;
    this.redraw();
  };

  update = () => {
    this.autoFilter = sheets.getById(this.cellsSheet.sheetId)?.autoFilter;
    this.redraw();
  };

  private redraw = () => {
    this.graphics.clear();
    this.controls = [];
    const autoFilter = this.autoFilter;
    if (!autoFilter) return;

    const sheet = sheets.getById(this.cellsSheet.sheetId);
    if (!sheet) return;

    const minX = Number(autoFilter.range.min.x);
    const maxX = Number(autoFilter.range.max.x);
    const headerY = Number(autoFilter.range.min.y);
    const filtered = new Set(autoFilter.filtered_columns);

    const border = getCSSVariableTint('border');
    const background = getCSSVariableTint('background');
    const caret = getCSSVariableTint('muted-foreground');
    const funnel = getCSSVariableTint('primary');

    for (let x = minX; x <= maxX; x++) {
      const cell = sheet.getCellOffsets(x, headerY);
      if (cell.height <= 0 || cell.width <= 0) continue;
      const size = Math.min(BUTTON_SIZE, cell.height - 2, cell.width - 2);
      if (size < 6) continue;
      const left = cell.x + cell.width - size - BUTTON_MARGIN;
      const top = cell.y + (cell.height - size) / 2;
      const columnIndex = x - minX;
      this.drawButton(
        left,
        top,
        size,
        border,
        background,
        filtered.has(columnIndex) ? funnel : caret,
        filtered.has(columnIndex)
      );
      this.controls.push({
        columnIndex,
        bounds: new Rectangle(left, top, size, size),
      });
    }
  };

  private drawButton = (
    x: number,
    y: number,
    size: number,
    border: number,
    background: number,
    icon: number,
    filtered: boolean
  ) => {
    this.graphics.lineStyle(1, border, 1);
    this.graphics.beginFill(background);
    this.graphics.drawRoundedRect(x, y, size, size, 2);
    this.graphics.endFill();

    const centerX = x + size / 2;
    const centerY = y + size / 2;
    this.graphics.lineStyle();
    this.graphics.beginFill(icon);
    if (filtered) {
      this.graphics.drawPolygon([
        centerX - 4,
        centerY - 4,
        centerX + 4,
        centerY - 4,
        centerX + 1,
        centerY - 0.5,
        centerX + 1,
        centerY + 4,
        centerX - 1,
        centerY + 4,
        centerX - 1,
        centerY - 0.5,
      ]);
    } else {
      this.graphics.drawPolygon([
        centerX - CARET_SIZE,
        centerY - CARET_SIZE / 2,
        centerX + CARET_SIZE,
        centerY - CARET_SIZE / 2,
        centerX,
        centerY + CARET_SIZE,
      ]);
    }
    this.graphics.endFill();
  };

  pointerMove = (world: Point): boolean => {
    const over = this.controls.some((control) => intersects.rectanglePoint(control.bounds, world));
    this.tableCursor = over ? 'pointer' : undefined;
    return over;
  };

  pointerDown = (world: Point): number | undefined => {
    const control = this.controls.find((control) => intersects.rectanglePoint(control.bounds, world));
    return control?.columnIndex;
  };
}
